import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [categories, setCategories] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollectionOpen, setIsCollectionOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/product_categories')
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        // Filter out private categories but allow chokolade for B2B
        // Filter out specific A6 and A7 categories to show a unified "Kort" link instead
        const filtered = data.filter(cat => 
          !cat.name.da.toLowerCase().includes('private') && 
          !cat.category_id.toLowerCase().includes('private') &&
          !cat.category_id.toLowerCase().includes('blomsterkort-a6') &&
          !cat.category_id.toLowerCase().includes('blomsterkort-a7')
        );
        setCategories(filtered);
      })
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

  const navLinks = [
    { name: 'Forside', href: '/' },
    { name: 'Forhandlere', href: '/forhandlere' },
    { name: 'Hvem er jeg', href: '/hvem-er-jeg' },
    { name: 'Priser', href: '/priser' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col xl:flex-row xl:justify-between xl:h-24 py-6 xl:py-0 items-start xl:items-center">
          <div className="flex items-center justify-between w-full xl:w-auto mb-6 xl:mb-0">
            {/* Logo */}
            <Link to="/" className="flex items-center relative">
              <span 
                className="text-3xl text-brand-950 transition-all duration-300 inline-block"
                style={{ fontFamily: "'Dancing Script', cursive", transform: "rotate(-3deg)" }}
              >
                CardsbyEve
              </span>
            </Link>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="text-slate-600 p-2"
                aria-label="Åbn menu"
              >
                <Menu />
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Nav */}
          <div className="hidden md:flex flex-wrap items-center gap-x-8 gap-y-4 xl:gap-x-10 xl:gap-y-0">
            <NavLink to="/" className={({ isActive }) => `text-xs uppercase tracking-widest font-medium transition-colors hover:text-brand-950 ${isActive ? 'text-brand-950' : 'text-brand-500'}`}>
              Forside
            </NavLink>

            {/* Products Dropdown */}
            <div 
              onMouseEnter={() => setIsDesktopDropdownOpen(true)}
              onMouseLeave={() => setIsDesktopDropdownOpen(false)}
              className="relative py-2"
            >
              <Link
                to="/produkter"
                className="flex items-center text-xs uppercase tracking-widest font-medium text-brand-500 hover:text-brand-950 transition-colors outline-none cursor-pointer"
              >
                Produkter <ChevronDown className="ml-1 w-3 h-3" />
              </Link>

              <AnimatePresence>
                {isDesktopDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute left-0 top-full mt-1 min-w-[220px] bg-white rounded-sm shadow-2xl border border-brand-100 p-2 z-50"
                  >
                    <Link
                      to="/produkter/blomsterkort"
                      className="block px-4 py-3 text-xs uppercase tracking-widest text-brand-600 hover:bg-brand-50 hover:text-brand-950 transition-colors outline-none"
                      onClick={() => setIsDesktopDropdownOpen(false)}
                    >
                      Blomsterkort
                    </Link>

                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={cat.category_id === 'chokolade' ? '/kollektion/chokolade' : `/produkter/${cat.category_id}`}
                        className="block px-4 py-3 text-xs uppercase tracking-widest text-brand-600 hover:bg-brand-50 hover:text-brand-950 transition-colors outline-none"
                        onClick={() => setIsDesktopDropdownOpen(false)}
                      >
                        {cat.name.da}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                className={({ isActive }) => `text-xs uppercase tracking-widest font-medium transition-colors hover:text-brand-950 ${isActive ? 'text-brand-950' : 'text-brand-500'}`}
              >
                {link.name}
              </NavLink>
            ))}

            <Link
              to="/kontakt?subject=book-visit"
              className="border border-brand-950 text-brand-950 px-8 py-2.5 text-xs uppercase tracking-widest font-medium hover:bg-brand-950 hover:text-white transition-all duration-300"
            >
              Book et besøg
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60] md:hidden"
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-[280px] bg-white shadow-2xl z-[70] md:hidden flex flex-col"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-50">
                <Link 
                  to="/" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center"
                >
                  <span 
                    className="text-2xl text-brand-950"
                    style={{ fontFamily: "'Dancing Script', cursive", transform: "rotate(-3deg)" }}
                  >
                    CardsbyEve
                  </span>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-slate-400 hover:text-brand-950 transition-colors p-2"
                  aria-label="Luk menu"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
                <div className="space-y-4">
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-xs uppercase tracking-widest font-medium text-brand-600 hover:text-brand-950"
                  >
                    Forside
                  </Link>
                  
                  <div className="">
                    <button 
                      onClick={() => setIsCollectionOpen(!isCollectionOpen)}
                      className="w-full flex items-center justify-between text-xs uppercase tracking-widest font-medium text-brand-600 hover:text-brand-950 outline-none"
                    >
                      <span>Produkter</span>
                      <motion.div
                        animate={{ rotate: isCollectionOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown size={14} />
                      </motion.div>
                    </button>
                    
                    <AnimatePresence initial={false}>
                      {isCollectionOpen && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-3 pl-2 border-l border-brand-50 mt-4 mb-2">
                            <Link
                      to="/produkter/blomsterkort"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-xs uppercase tracking-widest text-brand-500 hover:text-brand-950"
                            >
                              Blomsterkort
                            </Link>

                            {categories.map((cat) => (
                              <Link
                                key={cat.id}
                                to={cat.category_id === 'chokolade' ? '/kollektion/chokolade' : `/produkter/${cat.category_id}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block text-xs uppercase tracking-widest text-brand-500 hover:text-brand-950"
                              >
                                {cat.name.da}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {navLinks.slice(1).map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-xs uppercase tracking-widest font-medium text-brand-600 hover:text-brand-950"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-50">
                <Link
                  to="/kontakt?subject=book-visit"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center bg-brand-950 text-white px-6 py-4 text-xs uppercase tracking-widest font-medium hover:bg-brand-900 transition-all shadow-lg shadow-brand-950/10"
                >
                  Book et besøg
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
